import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  DropdownButton, Dropdown, InputGroup, Breadcrumb
} from 'react-bootstrap';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const AdmissionProcedureManager = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState(null);
  
  const [formData, setFormData] = useState({
    program_name: '',
    procedure_details: '',
    eligibility_criteria: '',
    required_documents: '',
    important_dates: '',
    contact_info: ''
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage
    const userData = JSON.parse(localStorage.getItem('admin_user') || 'null');
    setUser(userData);
    
    fetchProcedures();
  }, []);

  const resetForm = () => {
    setFormData({
      program_name: '',
      procedure_details: '',
      eligibility_criteria: '',
      required_documents: '',
      important_dates: '',
      contact_info: ''
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin/login';
  };

  const fetchProcedures = async () => {
  try {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('admin_token');
    
    console.log('🔍 Fetching admission procedures...');
    console.log('API URL:', `${API_BASE_URL}/admission_procedure.php?action=getAll`);
    console.log('Token exists:', !!token);
    
    const response = await fetch(`${API_BASE_URL}/admission_procedure.php?action=getAll`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    
    const text = await response.text();
    console.log('Raw response:', text);
    
    let data;
    try {
      data = JSON.parse(text);
      console.log('Parsed data:', data);
    } catch (parseError) {
      console.error('Failed to parse JSON:', parseError);
      setError('Invalid response from server');
      return;
    }
    
    if (data.success) {
      console.log('Data received:', data.data);
      setProcedures(data.data || []);
    } else {
      setError('Failed to load admission procedures: ' + (data.message || 'Unknown error'));
    }
  } catch (err) {
    console.error('Network error:', err);
    setError('Error connecting to server: ' + err.message);
  } finally {
    setLoading(false);
  }
};

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchProcedures();
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(
        `${API_BASE_URL}/admission_procedure.php?action=search&keyword=${encodeURIComponent(searchTerm)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      if (data.success) {
        setProcedures(data.data);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const userData = JSON.parse(localStorage.getItem('admin_user') || '{}');
      
      const payload = {
        ...formData,
        uploaded_by: userData.id || 1
      };
      
      let response;
      
      if (editingProcedure) {
        response = await fetch(`${API_BASE_URL}/admission_procedure.php?action=update&id=${editingProcedure.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API_BASE_URL}/admission_procedure.php?action=create`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(editingProcedure ? 'Procedure updated!' : 'Procedure added!');
        setShowModal(false);
        resetForm();
        setEditingProcedure(null);
        fetchProcedures();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (data.message || ''));
      }
    } catch (err) {
      setError('Error saving procedure: ' + err.message);
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this admission procedure?')) {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_BASE_URL}/admission_procedure.php?action=delete&id=${id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          setSuccess('Procedure deleted!');
          fetchProcedures();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete procedure: ' + (data.message || ''));
        }
      } catch (err) {
        setError('Error deleting procedure');
        console.error(err);
      }
    }
  };

  const handleEdit = (procedure) => {
    setEditingProcedure(procedure);
    setFormData({
      program_name: procedure.program_name || '',
      procedure_details: procedure.procedure_details || '',
      eligibility_criteria: procedure.eligibility_criteria || '',
      required_documents: procedure.required_documents || '',
      important_dates: procedure.important_dates || '',
      contact_info: procedure.contact_info || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProcedure(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderStatusBadge = (updatedDate) => {
    const updated = new Date(updatedDate);
    const now = new Date();
    const diffTime = Math.abs(now - updated);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 7) {
      return <Badge bg="success">Recently Updated</Badge>;
    } else if (diffDays <= 30) {
      return <Badge bg="info">Updated</Badge>;
    }
    return <Badge bg="secondary">Older</Badge>;
  };

  if (loading && procedures.length === 0) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading admission procedures...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item href="/admin">Dashboard</Breadcrumb.Item>
          <Breadcrumb.Item active>Admission Procedures</Breadcrumb.Item>
        </Breadcrumb>

        <Row className="mb-4">
          <Col>
            <h2>Admission Procedures Manager</h2>
            <p className="text-muted">Manage admission procedures for different programs</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add New Procedure
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card className="mb-4">
          <Card.Body>
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by program name, procedure details..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button variant="outline-secondary" onClick={handleSearch}>
                    <i className="fas fa-search"></i>
                  </Button>
                  {searchTerm && (
                    <Button variant="outline-danger" onClick={() => {
                      setSearchTerm('');
                      fetchProcedures();
                    }}>
                      <i className="fas fa-times"></i>
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col md={6} className="text-end">
                <Badge bg="light" text="dark" className="p-2">
                  Total: {procedures.length} procedures
                </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Program Name</th>
                    <th>Procedure Details</th>
                    <th>Eligibility</th>
                    <th>Contact Info</th>
                    <th>Last Updated</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {procedures.map((procedure) => (
                    <tr key={procedure.id}>
                      <td>
                        <strong>{procedure.program_name}</strong>
                        <div className="small text-muted">
                          ID: {procedure.id} | 
                          By: {procedure.uploaded_by_name || 'System'}
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          {truncateText(procedure.procedure_details, 80)}
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          {truncateText(procedure.eligibility_criteria, 60)}
                        </div>
                      </td>
                      <td>
                        {procedure.contact_info ? (
                          <div className="small text-truncate" style={{ maxWidth: '150px' }}>
                            {procedure.contact_info}
                          </div>
                        ) : (
                          <span className="text-muted small">Not specified</span>
                        )}
                      </td>
                      <td>
                        <div className="small">
                          <div>Updated: {formatDateTime(procedure.updated_at)}</div>
                          <div>Created: {formatDate(procedure.uploaded_at)}</div>
                        </div>
                      </td>
                      <td>
                        {renderStatusBadge(procedure.updated_at)}
                      </td>
                      <td>
                        <div className="d-flex">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleEdit(procedure)}
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(procedure.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {procedures.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-clipboard-list fa-3x mb-3"></i>
                <p>No admission procedures found.</p>
                <Button variant="primary" onClick={() => setShowModal(true)}>
                  Add Your First Procedure
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingProcedure ? 'Edit Admission Procedure' : 'Add Admission Procedure'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Program Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="program_name"
                  value={formData.program_name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Bachelor of Computer Science"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Procedure Details *</Form.Label>
                <Form.Control
                  as="textarea"
                  name="procedure_details"
                  value={formData.procedure_details}
                  onChange={handleTextareaChange}
                  rows={6}
                  required
                  placeholder="Step-by-step admission process..."
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Eligibility Criteria</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="eligibility_criteria"
                      value={formData.eligibility_criteria}
                      onChange={handleTextareaChange}
                      rows={4}
                      placeholder="Minimum qualifications, marks required..."
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Required Documents</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="required_documents"
                      value={formData.required_documents}
                      onChange={handleTextareaChange}
                      rows={4}
                      placeholder="Mark sheets, certificates, etc..."
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Important Dates</Form.Label>
                <Form.Control
                  as="textarea"
                  name="important_dates"
                  value={formData.important_dates}
                  onChange={handleTextareaChange}
                  rows={3}
                  placeholder="Application deadlines, exam dates..."
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Contact Information</Form.Label>
                <Form.Control
                  as="textarea"
                  name="contact_info"
                  value={formData.contact_info}
                  onChange={handleTextareaChange}
                  rows={2}
                  placeholder="Admission office contact details..."
                />
              </Form.Group>
              
              {editingProcedure && (
                <div className="alert alert-info mt-3">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Created by {editingProcedure.uploaded_by_name || 'System'} on {formatDate(editingProcedure.uploaded_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingProcedure ? 'Update' : 'Save'} Procedure
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default AdmissionProcedureManager;